import Image from "next/image";
import Link from "next/link";

import { isProd } from "@/Env";
import {
  HALLOWEEN_2026_COPY_READY,
  HALLOWEEN_2026_POPUP_CTA_HREF,
} from "@/constants/halloween-2026";
import { useHalloween2026 } from "@/hooks/useHalloween2026";
import { isDismissedToday } from "@/store/popup-banner-store";
import { useHalloween2026PopupStore } from "@/store/halloween-2026-popup-store";
import { useTrans } from "@/utils/useTrans";
import type { PopupSlide } from "@/v2-components/ui/floating-popup";

export const HALLOWEEN_2026_POPUP_BG =
  "/images/event/halloween-2026-popup-bg.webp";

/**
 * 홈 팝업의 할로윈 2026 슬라이드 제공자 (#6061 H1) — VRChat(`useTactGloveVrchatPopupSlides`)과 같은 형태다.
 * 껍데기(`FloatingPopup`)는 홈에 하나이고(`home-floating-popups.tsx`), 이 캠페인의 저장소 · 마크업만 여기 둔다.
 *
 * ```
 *   기간 안 + 오늘 안 닫음          → 슬라이드
 *   문구 전(COPY_READY false) · prod → 안 띄운다(빈 카드를 내보내지 않는다)
 *   문구 전 · 비-prod               → 띄운다(이름 · 배경만) — 시안 대조용
 * ```
 */
export function useHalloween2026PopupSlides(): PopupSlide[] {
  const { t } = useTrans(["gnb"]);
  const active = useHalloween2026();
  const dismissedUntil = useHalloween2026PopupStore((s) => s.dismissedUntil);
  const dismiss = useHalloween2026PopupStore((s) => s.dismiss);

  if (!active || isDismissedToday(dismissedUntil)) return [];
  if (!HALLOWEEN_2026_COPY_READY && isProd) return [];

  return [
    {
      key: "halloween-2026",
      label: t("halloween2026Name"),
      onDismissToday: dismiss,
      node: <Halloween2026Slide />,
    },
  ];
}

/**
 * Figma `Pop-up Card (Desktop) — Default` (`31437:7332`) · 모바일 시트 `31437:7404` — 360×190 슬롯 + 셸 푸터 40.
 *
 * ```
 *   배경   720×380 글자 없는 판 · cover · 대체색 Purple/100 #1C0F30
 *   글     왼쪽 16 · 세로 가운데 · 폭 180 · 블록 사이 16
 *     eyebrow  12/16 400 .4      white
 *     title    14/18 600 .25     white   (eyebrow 아래 4)
 *     CTA      h40 px24 r44 bg Blue/60 #0F62FE · 12/16 600 .1
 *     note     10/14 400 .4      white
 * ```
 *
 * ★글자를 이미지에 굽지 않는다 — 7개 언어로 나간다. 폭 180 은 오른쪽 그림(택트수트 · 호박)을 피하는 값이다.
 */
function Halloween2026Slide() {
  const { t } = useTrans(["gnb"]);
  const withCopy =
    HALLOWEEN_2026_COPY_READY && HALLOWEEN_2026_POPUP_CTA_HREF !== null;

  return (
    <div className="relative h-[190px] bg-[#1C0F30]">
      <Image
        src={HALLOWEEN_2026_POPUP_BG}
        alt=""
        fill
        sizes="360px"
        className="object-cover"
        priority
      />
      <div className="absolute inset-y-0 left-4 flex w-[180px] flex-col justify-center gap-4 text-white">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-400 tracking-[0.4px]">
            {t("halloween2026Name")}
          </p>
          {withCopy && (
            <p className="text-base font-600 tracking-[0.25px]">
              {t("halloween2026Message")}
            </p>
          )}
        </div>
        {withCopy && HALLOWEEN_2026_POPUP_CTA_HREF && (
          <>
            <Link
              href={HALLOWEEN_2026_POPUP_CTA_HREF}
              className="inline-flex h-10 w-fit items-center justify-center rounded-[44px] bg-[#0F62FE] px-6 text-sm font-600 tracking-[0.1px] text-white"
            >
              {t("halloween2026Cta")}
            </Link>
            <p className="text-2xs font-400 tracking-[0.4px]">
              {t("halloween2026Period")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
